import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Calendar } from "@/components/ui/calendar";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { axiosInstance } from "../App";
import { Plus, CheckCircle2, Clock, ListTree, Loader2, Calendar as CalendarIcon, User, Trash2, Edit } from "lucide-react";
import { toast } from "sonner";
import { format } from "date-fns";
import { tr } from "date-fns/locale";

function SubtaskList({ taskId, projectId, projectMembers = [], onUpdate }) {
  const [subtasks, setSubtasks] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingSubtask, setEditingSubtask] = useState(null);
  const [newSubtask, setNewSubtask] = useState({
    title: "",
    description: "",
    priority: "medium",
    assignee_id: null,
    due_date: null
  });
  const [creating, setCreating] = useState(false);
  const [deleting, setDeleting] = useState(null);

  useEffect(() => {
    fetchSubtasks();
    fetchUsers();
  }, [taskId]);

  const fetchSubtasks = async () => {
    try {
      const response = await axiosInstance.get(`/tasks/${taskId}/subtasks`);
      setSubtasks(response.data);
    } catch (error) {
      toast.error("Alt görevler yüklenemedi");
    } finally {
      setLoading(false);
    }
  };

  const fetchUsers = async () => {
    try {
      const response = await axiosInstance.get("/users");
      setUsers(response.data);
    } catch (error) {
      console.error("Kullanıcılar yüklenemedi", error);
    }
  };

  const handleCreateSubtask = async (e) => {
    e.preventDefault();
    if (!newSubtask.title.trim()) {
      toast.error("Başlık zorunludur");
      return;
    }

    setCreating(true);
    try {
      const payload = {
        title: newSubtask.title,
        description: newSubtask.description,
        priority: newSubtask.priority,
        assignee_id: newSubtask.assignee_id,
        due_date: newSubtask.due_date
      };
      
      await axiosInstance.post(`/tasks/${taskId}/subtasks`, payload);
      toast.success("Alt görev oluşturuldu");
      setDialogOpen(false);
      setNewSubtask({ 
        title: "", 
        description: "", 
        priority: "medium", 
        assignee_id: null, 
        due_date: null 
      });
      fetchSubtasks();
      if (onUpdate) onUpdate();
    } catch (error) {
      toast.error("Alt görev oluşturulamadı");
    } finally {
      setCreating(false);
    }
  };

  const handleUpdateSubtask = async (subtaskId, updates) => {
    try {
      await axiosInstance.put(`/tasks/${taskId}/subtasks/${subtaskId}`, updates);
      toast.success("Alt görev güncellendi");
      fetchSubtasks();
      if (onUpdate) onUpdate();
    } catch (error) {
      toast.error("Alt görev güncellenemedi");
    }
  };

  const handleDeleteSubtask = async (subtaskId) => {
    if (!confirm("Bu alt görevi silmek istediğinizden emin misiniz?")) return;
    
    setDeleting(subtaskId);
    try {
      await axiosInstance.delete(`/tasks/${taskId}/subtasks/${subtaskId}`);
      toast.success("Alt görev silindi");
      fetchSubtasks();
      if (onUpdate) onUpdate();
    } catch (error) {
      toast.error("Alt görev silinemedi");
    } finally {
      setDeleting(null);
    }
  };

  const getInitials = (name) => {
    if (!name) return "?";
    return name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2);
  };

  const getUserById = (userId) => {
    return users.find(u => u.id === userId);
  };

  const getPriorityColor = (priority) => {
    const colors = {
      high: "bg-red-100 text-red-800",
      medium: "bg-yellow-100 text-yellow-800",
      low: "bg-green-100 text-green-800"
    };
    return colors[priority] || colors.medium;
  };

  const getPriorityLabel = (priority) => {
    const labels = { high: "Yüksek", medium: "Orta", low: "Düşük" };
    return labels[priority] || priority;
  };

  const getStatusLabel = (status) => {
    const labels = { todo: "Yapılacak", "in-progress": "Devam Ediyor", done: "Tamamlandı" };
    return labels[status] || status;
  };

  const completedCount = subtasks.filter(s => s.status === "done").length;
  const progress = subtasks.length > 0 ? Math.round((completedCount / subtasks.length) * 100) : 0;

  if (loading) {
    return (
      <div className="flex justify-center py-4">
        <Loader2 className="h-6 w-6 animate-spin text-blue-500" />
      </div>
    );
  }

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <ListTree className="h-4 w-4" />
          <h4 className="font-semibold">Alt Görevler</h4>
          {subtasks.length > 0 && (
            <Badge variant="secondary">{subtasks.length}</Badge>
          )}
        </div>
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogTrigger asChild>
            <Button size="sm" variant="outline" className="gap-1" data-testid="add-subtask-button">
              <Plus className="h-3 w-3" />
              Alt Görev Ekle
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-2xl">
            <DialogHeader>
              <DialogTitle>Alt Görev Oluştur</DialogTitle>
              <DialogDescription>Ana görevin altında detaylı bir alt görev oluşturun</DialogDescription>
            </DialogHeader>
            <form onSubmit={handleCreateSubtask} className="space-y-4">
              <div>
                <label className="text-sm font-medium">Başlık *</label>
                <Input
                  value={newSubtask.title}
                  onChange={(e) => setNewSubtask({ ...newSubtask, title: e.target.value })}
                  placeholder="Alt görev başlığı"
                  required
                  data-testid="subtask-title-input"
                />
              </div>
              
              <div>
                <label className="text-sm font-medium">Açıklama</label>
                <Textarea
                  value={newSubtask.description}
                  onChange={(e) => setNewSubtask({ ...newSubtask, description: e.target.value })}
                  placeholder="Alt görev açıklaması (opsiyonel)"
                  rows={3}
                  data-testid="subtask-description-input"
                />
              </div>
              
              <div className="grid grid-cols-3 gap-4">
                <div>
                  <label className="text-sm font-medium">Öncelik</label>
                  <Select
                    value={newSubtask.priority}
                    onValueChange={(value) => setNewSubtask({ ...newSubtask, priority: value })}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="low">Düşük</SelectItem>
                      <SelectItem value="medium">Orta</SelectItem>
                      <SelectItem value="high">Yüksek</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                
                <div>
                  <label className="text-sm font-medium">Atanan Kişi</label>
                  <Select
                    value={newSubtask.assignee_id || "unassigned"}
                    onValueChange={(value) => setNewSubtask({ 
                      ...newSubtask, 
                      assignee_id: value === "unassigned" ? null : value 
                    })}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Seçiniz" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="unassigned">Atanmadı</SelectItem>
                      {users.map((user) => (
                        <SelectItem key={user.id} value={user.id}>
                          {user.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                
                <div>
                  <label className="text-sm font-medium">Bitiş Tarihi</label>
                  <Popover modal={true}>
                    <PopoverTrigger asChild>
                      <Button 
                        variant="outline" 
                        className="w-full justify-start text-left font-normal"
                      >
                        <CalendarIcon className="mr-2 h-4 w-4" />
                        {newSubtask.due_date ? format(new Date(newSubtask.due_date), "dd MMM yyyy", { locale: tr }) : "Seçiniz"}
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent 
                      className="w-auto p-0" 
                      style={{ zIndex: 999999 }}
                      align="start"
                      sideOffset={5}
                    >
                      <Calendar
                        mode="single"
                        selected={newSubtask.due_date ? new Date(newSubtask.due_date) : undefined}
                        onSelect={(date) => setNewSubtask({ 
                          ...newSubtask, 
                          due_date: date ? date.toISOString().split('T')[0] : null 
                        })}
                        initialFocus
                      />
                    </PopoverContent>
                  </Popover>
                </div>
              </div>
              
              <div className="flex gap-2 pt-2">
                <Button type="submit" className="flex-1" disabled={creating} data-testid="create-subtask-button">
                  {creating ? "Oluşturuluyor..." : "Oluştur"}
                </Button>
                <Button 
                  type="button" 
                  variant="outline" 
                  onClick={() => setDialogOpen(false)}
                  disabled={creating}
                >
                  İptal
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {subtasks.length > 0 && (
        <div className="space-y-2">
          <div className="flex items-center justify-between text-sm">
            <span className="text-gray-600">
              {completedCount}/{subtasks.length} tamamlandı
            </span>
            <span className="font-medium text-gray-900">{progress}%</span>
          </div>
          <Progress value={progress} className="h-2" />
        </div>
      )}

      {subtasks.length === 0 ? (
        <div className="text-center py-8 text-gray-500 border border-dashed rounded-lg">
          <ListTree className="h-8 w-8 mx-auto text-gray-300 mb-2" />
          <p className="text-sm">Henüz alt görev yok</p>
        </div>
      ) : (
        <div className="space-y-3">
          {subtasks.map((subtask) => {
            const assignee = getUserById(subtask.assignee_id);
            const isOverdue = subtask.due_date && new Date(subtask.due_date) < new Date() && subtask.status !== "done";
            
            return (
              <div
                key={subtask.id}
                className="p-4 border rounded-lg hover:bg-gray-50 transition-colors"
                data-testid={`subtask-${subtask.id}`}
              >
                <div className="flex items-start justify-between gap-3">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-2">
                      {subtask.status === "done" ? (
                        <CheckCircle2 className="h-5 w-5 text-green-500 flex-shrink-0" />
                      ) : (
                        <Clock className="h-5 w-5 text-gray-400 flex-shrink-0" />
                      )}
                      <p className={`text-sm font-semibold ${
                        subtask.status === "done" ? "line-through text-gray-400" : "text-gray-900"
                      }`}>
                        {subtask.title}
                      </p>
                    </div>
                    
                    {subtask.description && (
                      <p className="text-sm text-gray-600 mb-2 ml-7">{subtask.description}</p>
                    )}
                    
                    <div className="flex items-center gap-3 ml-7 flex-wrap">
                      <Badge className={getPriorityColor(subtask.priority)} variant="secondary">
                        {getPriorityLabel(subtask.priority)}
                      </Badge>
                      
                      {assignee && (
                        <div className="flex items-center gap-1.5">
                          <Avatar className="h-5 w-5">
                            <AvatarFallback className="text-xs bg-blue-100 text-blue-700">
                              {getInitials(assignee.name)}
                            </AvatarFallback>
                          </Avatar>
                          <span className="text-xs text-gray-600">{assignee.name}</span>
                        </div>
                      )}
                      
                      {subtask.due_date && (
                        <div className={`flex items-center gap-1 text-xs ${
                          isOverdue ? "text-red-600 font-medium" : "text-gray-600"
                        }`}>
                          <CalendarIcon className="h-3 w-3" />
                          {format(new Date(subtask.due_date), "dd MMM yyyy", { locale: tr })}
                        </div>
                      )}
                    </div>
                  </div>
                  
                  <div className="flex items-center gap-2">
                    <Select
                      value={subtask.status}
                      onValueChange={(value) => handleUpdateSubtask(subtask.id, { status: value })}
                    >
                      <SelectTrigger className="w-36 h-9 text-xs">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="todo">Yapılacak</SelectItem>
                        <SelectItem value="in-progress">Devam Ediyor</SelectItem>
                        <SelectItem value="done">Tamamlandı</SelectItem>
                      </SelectContent>
                    </Select>
                    
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => handleDeleteSubtask(subtask.id)}
                      disabled={deleting === subtask.id}
                      className="h-9 w-9 p-0"
                    >
                      {deleting === subtask.id ? (
                        <Loader2 className="h-4 w-4 animate-spin" />
                      ) : (
                        <Trash2 className="h-4 w-4 text-red-500" />
                      )}
                    </Button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

export default SubtaskList;
